<script setup lang="ts">
import type { ToolCallSummary } from "@maimu/contracts";
import { computed } from "vue";

const props = defineProps<{ calls: (ToolCallSummary & { running?: boolean })[] }>();

/**
 * The Phase-1 "Kasutatud allikad" card generalised to *which tools ran*
 * (reference §11, mAImu deltas). `report_grounding` is bookkeeping, not a source.
 */
const visible = computed(() => props.calls.filter((call) => call.tool !== "report_grounding"));

function freshness(call: ToolCallSummary): string | null {
  if (!call.fetchedAt) return null;
  const time = new Date(call.fetchedAt).toLocaleTimeString("et-EE", { hour: "2-digit", minute: "2-digit" });
  return call.cached ? `vahemälust, seisuga ${time}` : `seisuga ${time}`;
}
</script>

<template>
  <div v-if="visible.length" class="trail">
    <div class="heading">Kasutatud andmed</div>
    <ul>
      <li v-for="call in visible" :key="call.id" :class="{ failed: !call.running && !call.ok }">
        <span class="dot" :class="call.running ? 'running' : call.ok ? 'ok' : 'failed'" />
        <span class="label">{{ call.label }}</span>
        <span v-if="call.running" class="meta">…</span>
        <template v-else>
          <span v-if="call.summary" class="meta">{{ call.summary }}</span>
          <span v-if="call.error" class="meta error">{{ call.error }}</span>
          <span v-if="freshness(call)" class="pill">{{ freshness(call) }}</span>
          <span class="meta duration">{{ call.durationMs }} ms</span>
        </template>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.trail {
  margin-top: 0.85rem;
  padding-top: 0.7rem;
  border-top: 1px dashed var(--line);
}

.heading {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 0.4rem;
}

ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

li {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
  font-size: 0.82rem;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
  background: var(--muted);
}

.dot.ok {
  background: var(--green);
}

.dot.failed {
  background: var(--warn);
}

.dot.running {
  background: var(--gold);
  animation: pulse 1s ease-in-out infinite;
}

.label {
  color: var(--ink);
}

.meta {
  color: var(--muted);
  font-size: 0.78rem;
}

.meta.error {
  color: var(--warn);
}

.duration {
  opacity: 0.7;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
</style>
